import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Moon, LogOut, Mail, Loader2, Star, Check, Undo2, Gift, Send, ShieldAlert } from "lucide-react";
import LogoType from "@/components/LogoType";
import { useAccount } from "@/context/AccountAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const withCreds = { withCredentials: true };

const mailtoLink = (email, subject, body) =>
  `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

function GoogleGlyph() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

function GateView({ mode, onLogin, onSignOut }) {
  const forbidden = mode === "forbidden";
  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="gold-frame rounded-sm bg-[#1F130E] p-10 w-full max-w-md text-center" data-testid="admin-gate">
        <div className="flex flex-col items-center mb-6">
          {forbidden ? (
            <ShieldAlert className="w-8 h-8 text-[#C77E8A] mb-4" strokeWidth={1.3} />
          ) : (
            <Moon className="w-8 h-8 text-[#C9A24B] glow-gold mb-4" strokeWidth={1.2} />
          )}
          <span className="text-[11px] tracking-[0.4em] uppercase text-[#C9A24B]">Private Portal</span>
        </div>
        {forbidden ? (
          <>
            <h1 className="font-display text-3xl text-[#EFE3CC] mb-3">Not authorized</h1>
            <p className="text-sm text-[#B7A488] font-light mb-8">
              This Google account isn&apos;t permitted to access the admin dashboard.
            </p>
            <button
              onClick={onSignOut}
              data-testid="admin-signout-forbidden"
              className="inline-flex items-center gap-2 rounded-full border border-[#C9A24B]/40 px-6 py-3 text-xs tracking-[0.2em] uppercase text-[#B7A488] hover:text-[#E9D3A0] hover:border-[#C9A24B] transition-all"
            >
              <LogOut className="w-3.5 h-3.5" strokeWidth={1.6} /> Sign out & try another
            </button>
          </>
        ) : (
          <>
            <h1 className="font-display text-3xl text-[#EFE3CC] mb-3">Fairy Yasha</h1>
            <p className="text-sm text-[#B7A488] font-light mb-8">
              Sign in with your Google account to manage readings.
            </p>
            <button
              onClick={onLogin}
              data-testid="admin-google-signin"
              className="inline-flex items-center gap-3 rounded-full bg-[#EFE3CC] px-8 py-4 text-xs tracking-[0.2em] uppercase text-[#140B09] transition-all duration-300 hover:bg-white"
            >
              <GoogleGlyph /> Continue with Google
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function BookingCard({ b, onLogout }) {
  const [status, setStatus] = useState(b.status);
  const [note, setNote] = useState(b.note || "");
  const [saving, setSaving] = useState(false);
  const answered = status === "answered";

  const patch = async (body, msg) => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/admin/bookings/${b.id}`, body, withCreds);
      setStatus(data.status);
      setNote(data.note || "");
      if (msg) toast.success(msg);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) onLogout();
      else toast.error("Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`rounded-sm border bg-[#1F130E] p-6 transition-colors ${answered ? "border-[#C9A24B]/40" : "border-[#C9A24B]/18"}`} data-testid={`booking-${b.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-display text-2xl text-[#EFE3CC]">{b.name}</span>
          <span className="text-xs px-2.5 py-1 rounded-full border border-[#C9A24B]/30 text-[#C9A24B] tracking-wide">{b.service}</span>
          {b.is_free_trial ? (
            <span className="inline-flex items-center gap-1.5 text-[10px] px-2.5 py-1 rounded-full tracking-[0.15em] uppercase bg-[#C9A24B]/15 text-[#E9D3A0] border border-[#C9A24B]/40" data-testid={`free-trial-tag-${b.id}`}>
              <Gift className="w-3 h-3" strokeWidth={1.6} /> Free Trial
            </span>
          ) : b.had_free_reading ? (
            <span className="inline-flex items-center gap-1.5 text-[10px] px-2.5 py-1 rounded-full tracking-[0.15em] uppercase bg-[#9C2A52]/15 text-[#C77E8A] border border-[#C77E8A]/40" data-testid={`had-free-tag-${b.id}`}>
              <Gift className="w-3 h-3" strokeWidth={1.6} /> Had free reading
            </span>
          ) : null}
          <span className={`text-[10px] px-2.5 py-1 rounded-full tracking-[0.15em] uppercase ${answered ? "bg-[#C9A24B]/15 text-[#E9D3A0] border border-[#C9A24B]/40" : "bg-[#9C2A52]/15 text-[#C77E8A] border border-[#C77E8A]/40"}`} data-testid={`status-${b.id}`}>
            {answered ? "Answered" : "Pending"}
          </span>
        </div>
        <span className="text-xs text-[#7a6a55]">{new Date(b.created_at).toLocaleString()}</span>
      </div>

      <a href={`mailto:${b.email}`} className="inline-flex items-center gap-2 text-sm text-[#C77E8A] hover:text-[#E9D3A0] transition-colors mb-3">
        <Mail className="w-4 h-4" strokeWidth={1.5} /> {b.email}
      </a>
      <p className="text-[#CDBE9F] leading-relaxed mb-5">{b.question}</p>

      <div className="pt-4 border-t border-[#C9A24B]/12">
        <label className="block text-[11px] tracking-[0.15em] uppercase text-[#B7A488] mb-2">Private note</label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          data-testid={`note-${b.id}`}
          placeholder="Jot a private note to yourself…"
          className="w-full rounded-sm bg-[#140B09] border border-[#C9A24B]/25 px-4 py-3 text-sm text-[#EFE3CC] placeholder:text-[#7a6a55] outline-none focus:border-[#C77E8A] focus:ring-1 focus:ring-[#C77E8A]/40 transition-all resize-none"
        />
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            onClick={() => patch({ status: answered ? "pending" : "answered", note }, answered ? "Marked as pending" : "Marked as answered")}
            disabled={saving}
            data-testid={`toggle-status-${b.id}`}
            className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase transition-all disabled:opacity-60 ${answered ? "border border-[#C9A24B]/40 text-[#B7A488] hover:text-[#E9D3A0]" : "bg-gradient-to-r from-[#9C2A52] to-[#C77E8A] text-[#EFE3CC] hover:scale-[1.03]"}`}
          >
            {answered ? <><Undo2 className="w-4 h-4" strokeWidth={1.5} /> Reopen</> : <><Check className="w-4 h-4" strokeWidth={1.5} /> Mark answered</>}
          </button>
          <button
            onClick={() => patch({ note }, "Note saved")}
            disabled={saving}
            data-testid={`save-note-${b.id}`}
            className="rounded-full border border-[#C9A24B]/40 px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase text-[#EFE3CC] hover:border-[#C9A24B] hover:bg-[#C9A24B]/10 transition-all disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save note"}
          </button>
          <a
            href={mailtoLink(
              b.email,
              "Your reading from Fairy Yasha ✦",
              `Dear ${b.name},\n\nThank you for reaching out. Here is your reading:\n\n\n\nWith love,\nFairy Yasha ✦`
            )}
            data-testid={`send-response-${b.id}`}
            className="flex items-center gap-2 rounded-full border border-[#C77E8A]/50 px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase text-[#C77E8A] hover:text-[#E9D3A0] hover:border-[#C77E8A] transition-all"
          >
            <Send className="w-3.5 h-3.5" strokeWidth={1.6} /> Send Response
          </a>
        </div>
      </div>
    </div>
  );
}

function AppointmentRow({ a, onReload, onLogout }) {
  const isPendulum = !!a.delivery_method;
  const questions = a.questions || [];
  const [answers, setAnswers] = useState(() =>
    questions.map((_, i) => (a.answers && a.answers[i]) || "")
  );
  const [busy, setBusy] = useState(false);

  const setAns = (i, v) => setAnswers((arr) => arr.map((x, idx) => (idx === i ? v : x)));

  const patchAppt = async (body, msg) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/appointments/${a.id}`, body, withCreds);
      if (msg) toast.success(msg);
      onReload();
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) onLogout();
      else toast.error("Could not save. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-sm border border-[#C9A24B]/25 bg-[#231320] p-6" data-testid={`appointment-${a.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-3">
          <span className="font-display text-2xl text-[#EFE3CC]">{a.name}</span>
          <span className="text-xs px-2.5 py-1 rounded-full bg-[#C9A24B]/15 border border-[#C9A24B]/40 text-[#E9D3A0] tracking-wide">{a.tier_name} · ${a.amount}</span>
          <span className="text-[10px] px-2.5 py-1 rounded-full tracking-[0.15em] uppercase border border-[#C77E8A]/40 text-[#C77E8A]" data-testid={`appt-status-${a.id}`}>{a.status || "pending"}</span>
        </div>
        {isPendulum ? (
          <span className="text-xs text-[#C77E8A]" data-testid={`appt-delivery-${a.id}`}>
            {a.delivery_method} · {questions.length} question{questions.length > 1 ? "s" : ""}
          </span>
        ) : (
          <span className="text-xs text-[#C77E8A]">{a.date} · {a.time}</span>
        )}
      </div>
      <a href={`mailto:${a.email}`} className="inline-flex items-center gap-2 text-sm text-[#C77E8A] hover:text-[#E9D3A0] transition-colors">
        <Mail className="w-4 h-4" strokeWidth={1.5} /> {a.email}
      </a>

      {isPendulum ? (
        <div className="mt-4 space-y-3" data-testid={`appt-answers-${a.id}`}>
          {questions.map((q, qi) => (
            <div key={qi} className="rounded-sm bg-[#140B09]/60 p-3 border border-[#C9A24B]/12">
              <p className="text-sm text-[#EFE3CC] mb-2"><span className="text-[#C9A24B]">Q{qi + 1}.</span> {q}</p>
              <input
                value={answers[qi]}
                onChange={(e) => setAns(qi, e.target.value)}
                data-testid={`appt-answer-input-${a.id}-${qi}`}
                placeholder="Your answer (e.g., Yes — the energy is favourable)"
                className="w-full rounded-sm bg-[#140B09] border border-[#C9A24B]/25 px-3 py-2 text-sm text-[#EFE3CC] placeholder:text-[#7a6a55] outline-none focus:border-[#C77E8A] focus:ring-1 focus:ring-[#C77E8A]/40 transition-all"
              />
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <button
              onClick={() => patchAppt({ answers }, "Draft saved")}
              disabled={busy}
              data-testid={`appt-save-draft-${a.id}`}
              className="rounded-full border border-[#C9A24B]/40 px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase text-[#EFE3CC] hover:border-[#C9A24B] hover:bg-[#C9A24B]/10 transition-all disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save draft"}
            </button>
            <button
              onClick={() => {
                if (answers.every((x) => !x.trim())) { toast.error("Please write at least one answer first."); return; }
                patchAppt({ answers, send_email: true }, "Answers emailed to client ✦");
              }}
              disabled={busy}
              data-testid={`appt-send-answers-${a.id}`}
              className="flex items-center gap-2 rounded-full bg-gradient-to-r from-[#9C2A52] to-[#C77E8A] px-6 py-2.5 text-[11px] tracking-[0.18em] uppercase text-[#EFE3CC] transition-transform hover:scale-[1.03] disabled:opacity-60"
            >
              <Send className="w-3.5 h-3.5" strokeWidth={1.6} /> Send Answers to Client
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-4">
          {a.note ? <p className="mb-3 text-[#CDBE9F] leading-relaxed">{a.note}</p> : null}
          <a
            href={mailtoLink(
              a.email,
              `Your ${a.tier_name} reading ✦`,
              `Dear ${a.name},\n\nLooking forward to your ${a.tier_name} on ${a.date} at ${a.time}.\n\n\n\nWith love,\nFairy Yasha ✦`
            )}
            data-testid={`send-response-appt-${a.id}`}
            className="inline-flex items-center gap-2 rounded-full border border-[#C77E8A]/50 px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase text-[#C77E8A] hover:text-[#E9D3A0] hover:border-[#C77E8A] transition-all"
          >
            <Send className="w-3.5 h-3.5" strokeWidth={1.6} /> Send Response
          </a>
        </div>
      )}
    </div>
  );
}

function DashboardView({ onLogout }) {
  const [bookings, setBookings] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [orders, setOrders] = useState(null);

  const load = useCallback(async () => {
    try {
      const [b, a, o] = await Promise.all([
        axios.get(`${API}/admin/bookings`, withCreds),
        axios.get(`${API}/admin/appointments`, withCreds),
        axios.get(`${API}/admin/orders`, withCreds),
      ]);
      setBookings(b.data);
      setAppointments(a.data);
      setOrders(o.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) onLogout();
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen">
      <header className="border-b border-[#C9A24B]/15 bg-[#140B09]/85 backdrop-blur-xl sticky top-0 z-10">
        <div className="mx-auto max-w-[1000px] px-6 h-20 flex items-center justify-between">
          <Link to="/"><LogoType size="sm" /></Link>
          <button onClick={onLogout} data-testid="admin-logout" className="flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-[#B7A488] hover:text-[#E9D3A0] transition-colors">
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-[1000px] px-6 py-14">
        {/* Orders & revenue */}
        {orders && (
          <div className="mb-16" data-testid="admin-orders">
            <div className="flex items-center gap-3 mb-6">
              <Star className="w-4 h-4 text-[#C9A24B]" fill="#C9A24B" strokeWidth={0} />
              <h1 className="font-display text-4xl text-[#EFE3CC]">Orders & Revenue</h1>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
              <div className="rounded-sm border border-[#C9A24B]/25 bg-[#231320] p-5">
                <p className="text-[11px] tracking-[0.2em] uppercase text-[#B7A488]">Total Revenue</p>
                <p className="mt-1 font-display text-4xl text-gold-gradient" data-testid="orders-total-revenue">${orders.total_revenue.toFixed(2)}</p>
              </div>
              <div className="rounded-sm border border-[#C9A24B]/25 bg-[#231320] p-5">
                <p className="text-[11px] tracking-[0.2em] uppercase text-[#B7A488]">Paid Orders</p>
                <p className="mt-1 font-display text-4xl text-[#EFE3CC]" data-testid="orders-count">{orders.count}</p>
              </div>
            </div>
            {orders.orders.length > 0 && (
              <div className="rounded-sm border border-[#C9A24B]/18 divide-y divide-[#C9A24B]/10" data-testid="orders-list">
                {orders.orders.map((o) => (
                  <div key={o.session_id} className="flex items-center justify-between gap-4 px-5 py-3.5 text-sm">
                    <span className="text-[#EFE3CC] flex-1 truncate">{o.tier_name}{o.quantity > 1 ? ` × ${o.quantity}` : ""}</span>
                    <span className="text-[#B7A488] text-xs hidden sm:block">{(o.updated_at || "").slice(0, 10)}</span>
                    <span className="text-gold-gradient font-display text-lg">${Number(o.amount || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Paid, scheduled sessions */}
        <div className="flex items-center gap-3 mb-8">
          <Star className="w-4 h-4 text-[#C9A24B]" fill="#C9A24B" strokeWidth={0} />
          <h1 className="font-display text-4xl text-[#EFE3CC]">Booked Sessions</h1>
          <span className="text-sm text-[#B7A488]">({appointments.length})</span>
        </div>
        {appointments.length === 0 ? (
          <p className="text-[#B7A488] mb-14">No paid sessions scheduled yet.</p>
        ) : (
          <div className="space-y-4 mb-16" data-testid="admin-appointments-list">
            {appointments.map((a) => (
              <AppointmentRow key={a.id} a={a} onReload={load} onLogout={onLogout} />
            ))}
          </div>
        )}

        {/* Inquiries */}
        <div className="flex items-center gap-3 mb-10">
          <Star className="w-4 h-4 text-[#C9A24B]" fill="#C9A24B" strokeWidth={0} />
          <h1 className="font-display text-4xl text-[#EFE3CC]">Reading Requests</h1>
          <span className="text-sm text-[#B7A488]">({bookings ? bookings.length : "…"})</span>
        </div>

        {bookings === null ? (
          <div className="flex items-center gap-2 text-[#B7A488]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : bookings.length === 0 ? (
          <p className="text-[#B7A488]">No requests yet.</p>
        ) : (
          <div className="space-y-4" data-testid="admin-bookings-list">
            {bookings.map((b) => (
              <BookingCard key={b.id} b={b} onLogout={onLogout} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, loading, login, logout } = useAccount();
  // 'checking' | 'authorized' | 'forbidden' | 'anon'
  const [state, setState] = useState("checking");

  useEffect(() => {
    document.title = "Admin · Fairy Yasha";
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      setState("anon");
      return;
    }
    let active = true;
    axios
      .get(`${API}/admin/me`, withCreds)
      .then(() => active && setState("authorized"))
      .catch((e) => active && setState(e.response?.status === 403 ? "forbidden" : "anon"));
    return () => {
      active = false;
    };
  }, [user, loading]);

  const handleSignOut = async () => {
    await logout();
    setState("anon");
  };

  if (loading || state === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <Loader2 className="w-6 h-6 text-[#C9A24B] animate-spin" />
      </div>
    );
  }
  if (state === "authorized") {
    return <DashboardView onLogout={handleSignOut} />;
  }
  return (
    <GateView
      mode={state === "forbidden" ? "forbidden" : "anon"}
      onLogin={() => login("/admin")}
      onSignOut={handleSignOut}
    />
  );
}
